/**
 * Audio Utility
 * Dynamic synthesizers using HTML5 Web Audio API.
 * Provides lightweight and beautiful UI feedback sounds without needing external audio asset downloads.
 */

/**
 * Plays a highly-recognizable premium Tri-Tone success chime
 * Mimics the classic iOS tri-tone notification (G5 -> C6 -> E6) using a soft, sweet wood-chime tone.
 * Perfect for success actions, checkmarks, or status changes.
 */
export function playSuccessChime() {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const t = ctx.currentTime;
    
    // Tone 1: G5 (784 Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'triangle'; // triangle wave has a sweet wood-like tone
    osc1.frequency.setValueAtTime(783.99, t);
    gain1.gain.setValueAtTime(0.08, t);
    gain1.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    
    // Tone 2: C6 (1046.5 Hz)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'triangle';
    osc2.frequency.setValueAtTime(1046.50, t + 0.07);
    gain2.gain.setValueAtTime(0.08, t + 0.07);
    gain2.gain.exponentialRampToValueAtTime(0.001, t + 0.22);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    
    // Tone 3: E6 (1318.5 Hz)
    const osc3 = ctx.createOscillator();
    const gain3 = ctx.createGain();
    osc3.type = 'triangle';
    osc3.frequency.setValueAtTime(1318.51, t + 0.14);
    gain3.gain.setValueAtTime(0.1, t + 0.14);
    gain3.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
    osc3.connect(gain3);
    gain3.connect(ctx.destination);
    
    // Start sequence
    osc1.start(t);
    osc2.start(t + 0.07);
    osc3.start(t + 0.14);
    
    // Stop sequence
    osc1.stop(t + 0.15);
    osc2.stop(t + 0.22);
    osc3.stop(t + 0.35);
  } catch (err) {
    console.warn('Web Audio API chime could not play due to browser user-gesture requirements:', err);
  }
}

/**
 * Plays a modern, crisp bubble click pop
 * Mimics popular UI check/switch taps (e.g. Slack/Instagram bubble tap).
 * Perfect for generic status updates, modal toggle confirmations.
 */
export function playSoftPop() {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();
    const t = ctx.currentTime;
    
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = 'sine';
    // Very fast pitch sweep from 600Hz to 150Hz in 40ms creates a crisp "pop"
    osc.frequency.setValueAtTime(600, t);
    osc.frequency.exponentialRampToValueAtTime(150, t + 0.04);
    
    gain.gain.setValueAtTime(0.04, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.04);
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    
    osc.start(t);
    osc.stop(t + 0.05);
  } catch (e) {
    // Ignore
  }
}
